import type { CSSProperties, ReactNode } from 'react'
import { useMemo, useState } from 'react'
import { ChevronDown, Radar, X, Zap } from 'lucide-react'

import {
  buildCadenceSummary,
  listToMultiline,
  normalizeWorkflowSpec,
  parseMultilineList,
  type RecurringTaskProposal,
  workflowActivationCopy,
  workflowActivationLabel,
  workflowHealthLabel,
  workflowKindAccent,
  workflowKindLabel,
  workflowReadiness,
  type WorkflowKind,
} from '@/features/recurring/workflow'
import { categoryColor } from '@/hooks/useRecurringTasks'

const PRESET_CATEGORIES = [
  'Annonsering', 'Innhold', 'Rapportering', 'E-post',
  'Sosiale medier', 'Analyse', 'Møteforberedelse',
  'Kundeoppfølging', 'Vedlikehold', 'Tilpasset',
]

const CONNECTORS = [
  { id: 'github', name: 'GitHub' },
  { id: 'gmail', name: 'Gmail' },
  { id: 'meta-ads', name: 'Meta Ads' },
  { id: 'instagram', name: 'Instagram' },
  { id: 'outlook', name: 'Outlook Mail' },
  { id: 'gcal', name: 'Google Calendar' },
  { id: 'ocal', name: 'Outlook Calendar' },
]

const WEEKDAYS_NO = ['Mandag', 'Tirsdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lørdag', 'Søndag']
const WORKFLOW_KIND_OPTIONS: WorkflowKind[] = ['monitor', 'report', 'reminder', 'sync']

interface CreateTaskModalProps {
  onClose: () => void
  onCreate: (proposal: RecurringTaskProposal) => Promise<void>
  initialValues?: Partial<RecurringTaskProposal>
}

export function CreateTaskModal({ onClose, onCreate, initialValues }: CreateTaskModalProps) {
  const [title, setTitle] = useState(initialValues?.title ?? '')
  const [description, setDescription] = useState(initialValues?.description ?? '')
  const [category, setCategory] = useState(initialValues?.category ?? 'Tilpasset')
  const [customCategory, setCustomCategory] = useState('')
  const [frequency, setFrequency] = useState<RecurringTaskProposal['frequency']>(initialValues?.frequency ?? 'weekly')
  const [dayOfWeek, setDayOfWeek] = useState(initialValues?.dayOfWeek ?? 0)
  const [dayOfMonth, setDayOfMonth] = useState(initialValues?.dayOfMonth ?? 1)
  const [customDays, setCustomDays] = useState<number[]>(initialValues?.customDays ?? [])
  const [timeOfDay, setTimeOfDay] = useState(initialValues?.timeOfDay ?? '09:00')
  const [connectorId, setConnectorId] = useState(initialValues?.connectorId ?? '')
  const [agentPrompt, setAgentPrompt] = useState(initialValues?.agentPrompt ?? '')
  const [endsAt, setEndsAt] = useState(initialValues?.endsAt ?? '')
  const [neverEnds, setNeverEnds] = useState(!initialValues?.endsAt)
  const [workflowKind, setWorkflowKind] = useState<WorkflowKind>(initialValues?.workflowSpec?.kind ?? 'monitor')
  const [objective, setObjective] = useState(initialValues?.workflowSpec?.objective ?? initialValues?.description ?? '')
  const [sourceSummary, setSourceSummary] = useState(initialValues?.workflowSpec?.sourceSummary ?? '')
  const [checksInput, setChecksInput] = useState(listToMultiline(initialValues?.workflowSpec?.checks))
  const [alertsInput, setAlertsInput] = useState(listToMultiline(initialValues?.workflowSpec?.alertConditions))
  const [destinationsInput, setDestinationsInput] = useState(listToMultiline(initialValues?.workflowSpec?.destinations))
  const [rollup, setRollup] = useState(initialValues?.workflowSpec?.rollup ?? '')
  const [activationMode, setActivationMode] = useState<'auto_run' | 'manual_review'>(initialValues?.workflowSpec?.activationMode ?? 'auto_run')
  const [saving, setSaving] = useState(false)

  const finalCategory = category === 'Tilpasset' && customCategory.trim() ? customCategory.trim() : category
  const color = categoryColor(finalCategory)
  const accent = workflowKindAccent(workflowKind)

  const cadenceSummary = useMemo(() => buildCadenceSummary({
    frequency,
    dayOfWeek,
    dayOfMonth,
    customDays,
    timeOfDay,
  }), [customDays, dayOfMonth, dayOfWeek, frequency, timeOfDay])

  const draftWorkflowSpec = useMemo(() => normalizeWorkflowSpec({
    description,
    connectorName: CONNECTORS.find(item => item.id === connectorId)?.name,
    frequency,
    dayOfWeek,
    dayOfMonth,
    customDays,
    timeOfDay,
  }, {
    kind: workflowKind,
    objective: objective.trim() || undefined,
    sourceSummary: sourceSummary.trim() || undefined,
    checks: parseMultilineList(checksInput),
    alertConditions: parseMultilineList(alertsInput),
    destinations: parseMultilineList(destinationsInput),
    rollup: rollup.trim() || undefined,
    activationMode,
  }), [activationMode, alertsInput, checksInput, connectorId, customDays, dayOfMonth, dayOfWeek, description, destinationsInput, frequency, objective, rollup, sourceSummary, timeOfDay, workflowKind])

  const readiness = useMemo(() => workflowReadiness({
    title,
    description,
    agentPrompt,
    connectorId: connectorId || undefined,
    connectorName: CONNECTORS.find(item => item.id === connectorId)?.name,
    workflowSpec: draftWorkflowSpec,
  }), [agentPrompt, connectorId, description, draftWorkflowSpec, title])
  const canSave = readiness.isReady && title.trim().length > 0 && agentPrompt.trim().length > 0 && !saving

  const toggleCustomDay = (day: number) => {
    setCustomDays(previous => previous.includes(day) ? previous.filter(value => value !== day) : [...previous, day].sort())
  }

  const handleSave = async () => {
    if (!title.trim() || !agentPrompt.trim()) return
    setSaving(true)
    try {
      const connector = CONNECTORS.find(item => item.id === connectorId)
      await onCreate({
        title: title.trim(),
        description: description.trim(),
        category: finalCategory,
        color,
        frequency,
        dayOfWeek: frequency === 'weekly' ? dayOfWeek : undefined,
        dayOfMonth: frequency === 'monthly' ? dayOfMonth : undefined,
        customDays: frequency === 'custom' ? customDays : undefined,
        timeOfDay,
        connectorId: connectorId || undefined,
        connectorName: connector?.name,
        agentPrompt: agentPrompt.trim(),
        endsAt: neverEnds ? undefined : endsAt || undefined,
        workflowSpec: draftWorkflowSpec,
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: 'rgba(0,0,0,0.75)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backdropFilter: 'blur(4px)',
      }}
      onClick={event => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div style={{
        background: '#1A1A1A',
        borderRadius: 16,
        width: 720,
        maxWidth: '95vw',
        maxHeight: '90vh',
        overflowY: 'auto',
        padding: '28px 28px 24px',
        position: 'relative',
        border: '1px solid #2E2E2E',
      }}>
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: 16,
            right: 16,
            background: 'none',
            border: 'none',
            color: '#5A5A5A',
            cursor: 'pointer',
            padding: 4,
            borderRadius: 6,
          }}
        >
          <X size={18} />
        </button>

        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 20 }}>
          <div>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: '#E5E5E5', margin: '0 0 8px' }}>
              Ny recurring workflow
            </h2>
            <p style={{ margin: 0, fontSize: 13, color: '#8A8A8A', lineHeight: 1.55 }}>
              Sett opp trigger, signaler, varsler og leveranse som én fast agentisk flyt.
            </p>
          </div>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 7,
            padding: '8px 12px',
            borderRadius: 999,
            background: accent.background,
            border: `1px solid ${accent.border}`,
            color: accent.text,
            fontSize: 12,
            fontWeight: 700,
            flexShrink: 0,
          }}>
            <Radar size={14} />
            {workflowKindLabel(workflowKind)}
          </div>
        </div>

        <div style={{ display: 'grid', gap: 18 }}>
          <div style={{
            padding: '14px 16px',
            borderRadius: 14,
            background: 'rgba(255,255,255,0.03)',
            border: `1px solid ${accent.border}`,
          }}>
            <div style={{ fontSize: 11, color: '#7A7A7A', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
              Workflow-type
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {WORKFLOW_KIND_OPTIONS.map(option => {
                const optionAccent = workflowKindAccent(option)
                const active = workflowKind === option
                return (
                  <button
                    key={option}
                    onClick={() => setWorkflowKind(option)}
                    style={{
                      padding: '7px 12px',
                      borderRadius: 999,
                      border: `1px solid ${active ? optionAccent.border : '#333'}`,
                      background: active ? optionAccent.background : '#252525',
                      color: active ? optionAccent.text : '#9A9A9A',
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    {workflowKindLabel(option)}
                  </button>
                )
              })}
            </div>
          </div>

          <div style={{
            padding: '14px 16px',
            borderRadius: 14,
            background: 'rgba(255,255,255,0.03)',
            border: `1px solid ${readiness.isReady ? 'rgba(34,197,94,0.24)' : 'rgba(245,158,11,0.24)'}`,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 10 }}>
              <div>
                <div style={{ fontSize: 11, color: '#7A7A7A', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
                  Workflow QA
                </div>
                <div style={{ fontSize: 13, color: '#E5E5E5', fontWeight: 600 }}>
                  {workflowHealthLabel(readiness.score)} · {readiness.score}/100
                </div>
              </div>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px', borderRadius: 999, background: '#141414', border: '1px solid #2A2A2A', fontSize: 11, color: '#CFCFCF' }}>
                {workflowActivationLabel(activationMode)}
              </div>
            </div>
            {readiness.blockers.length > 0 && (
              <div style={{ marginBottom: readiness.warnings.length > 0 ? 8 : 0 }}>
                <div style={{ fontSize: 11, color: '#FCA5A5', fontWeight: 700, marginBottom: 4 }}>Må løses før aktivering</div>
                {readiness.blockers.map(item => (
                  <div key={item} style={{ fontSize: 12, color: '#FCA5A5', lineHeight: 1.5 }}>{item}</div>
                ))}
              </div>
            )}
            {readiness.warnings.length > 0 && (
              <div>
                <div style={{ fontSize: 11, color: '#FCD34D', fontWeight: 700, marginBottom: 4 }}>Bør forbedres</div>
                {readiness.warnings.map(item => (
                  <div key={item} style={{ fontSize: 12, color: '#D1D5DB', lineHeight: 1.5 }}>{item}</div>
                ))}
              </div>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1.1fr 0.9fr', gap: 16 }}>
            <Field label="Workflow-navn *">
              <input
                value={title}
                onChange={event => setTitle(event.target.value)}
                placeholder="f.eks. Ukentlig konkurrentmonitor"
                style={inputStyle}
                autoFocus
              />
            </Field>

            <Field label="Kategori">
              <div style={{ display: 'flex', gap: 8 }}>
                <div style={{ position: 'relative', flex: 1 }}>
                  <select
                    value={category}
                    onChange={event => setCategory(event.target.value)}
                    style={{ ...inputStyle, appearance: 'none', paddingRight: 30 }}
                  >
                    {PRESET_CATEGORIES.map(item => (
                      <option key={item} value={item}>{item}</option>
                    ))}
                  </select>
                  <ChevronDown size={13} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: '#5A5A5A', pointerEvents: 'none' }} />
                </div>
                <div style={{
                  width: 38,
                  height: 38,
                  borderRadius: 8,
                  flexShrink: 0,
                  background: `${color}33`,
                  border: `2px solid ${color}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <span style={{ width: 12, height: 12, borderRadius: '50%', background: color }} />
                </div>
              </div>
              {category === 'Tilpasset' && (
                <input
                  value={customCategory}
                  onChange={event => setCustomCategory(event.target.value)}
                  placeholder="Skriv inn kategorinavn..."
                  style={{ ...inputStyle, marginTop: 6 }}
                />
              )}
            </Field>
          </div>

          <Field label="Kort oppsummering">
            <textarea
              value={description}
              onChange={event => setDescription(event.target.value)}
              placeholder="Hva skal denne workflowen gjøre over tid?"
              rows={2}
              style={{ ...inputStyle, resize: 'vertical', minHeight: 64 }}
            />
          </Field>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <Field label="Workflow-mål">
              <textarea
                value={objective}
                onChange={event => setObjective(event.target.value)}
                placeholder="Hva skal den sikre, oppdage eller levere?"
                rows={3}
                style={{ ...inputStyle, resize: 'vertical', minHeight: 86 }}
              />
            </Field>

            <Field label="Kildesammendrag">
              <textarea
                value={sourceSummary}
                onChange={event => setSourceSummary(event.target.value)}
                placeholder="Hvilke systemer, dashboards eller signaler bygger den på?"
                rows={3}
                style={{ ...inputStyle, resize: 'vertical', minHeight: 86 }}
              />
            </Field>
          </div>

          <div style={{
            padding: '14px 16px',
            borderRadius: 14,
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.06)',
          }}>
            <div style={{ fontSize: 11, color: '#7A7A7A', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 }}>
              Trigger og rytme
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 140px 1fr', gap: 12, alignItems: 'end' }}>
              <Field label="Frekvens">
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {(['daily', 'weekly', 'monthly', 'custom'] as const).map(option => (
                    <button
                      key={option}
                      onClick={() => setFrequency(option)}
                      style={{
                        padding: '6px 14px',
                        borderRadius: 7,
                        border: 'none',
                        background: frequency === option ? '#F0F0F0' : '#252525',
                        color: frequency === option ? '#111' : '#9A9A9A',
                        fontSize: 12,
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        fontWeight: 500,
                      }}
                    >
                      {{ daily: 'Daglig', weekly: 'Ukentlig', monthly: 'Månedlig', custom: 'Tilpasset' }[option]}
                    </button>
                  ))}
                </div>
              </Field>

              <Field label="Tidspunkt">
                <input
                  type="time"
                  value={timeOfDay}
                  onChange={event => setTimeOfDay(event.target.value)}
                  style={inputStyle}
                />
              </Field>

              <Field label="Trigger-preview">
                <div style={{
                  minHeight: 38,
                  display: 'flex',
                  alignItems: 'center',
                  padding: '8px 12px',
                  borderRadius: 8,
                  background: '#141414',
                  border: '1px solid #2A2A2A',
                  color: '#CFCFCF',
                  fontSize: 12,
                }}>
                  {cadenceSummary}
                </div>
              </Field>
            </div>

            {frequency === 'weekly' && (
              <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
                {WEEKDAYS_NO.map((day, index) => (
                  <button
                    key={day}
                    onClick={() => setDayOfWeek(index)}
                    style={dayChipStyle(dayOfWeek === index)}
                  >
                    {day.slice(0, 3)}
                  </button>
                ))}
              </div>
            )}

            {frequency === 'monthly' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
                <span style={{ fontSize: 12, color: '#7A7A7A' }}>Den</span>
                <input
                  type="number"
                  min={1}
                  max={31}
                  value={dayOfMonth}
                  onChange={event => setDayOfMonth(Number(event.target.value))}
                  style={{ ...inputStyle, width: 72 }}
                />
                <span style={{ fontSize: 12, color: '#7A7A7A' }}>hver måned</span>
              </div>
            )}

            {frequency === 'custom' && (
              <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
                {WEEKDAYS_NO.map((day, index) => (
                  <button
                    key={day}
                    onClick={() => toggleCustomDay(index)}
                    style={dayChipStyle(customDays.includes(index))}
                  >
                    {day.slice(0, 3)}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <Field label="Hva den sjekker">
              <textarea
                value={checksInput}
                onChange={event => setChecksInput(event.target.value)}
                placeholder={'Én sjekk per linje\nSe etter nye endringer i konkurrenters priser\nHent ukens viktigste signaler'}
                rows={5}
                style={{ ...inputStyle, resize: 'vertical', minHeight: 120 }}
              />
            </Field>

            <Field label="Når den varsler">
              <textarea
                value={alertsInput}
                onChange={event => setAlertsInput(event.target.value)}
                placeholder={'Én alert condition per linje\nVarsle hvis pris endres mer enn 10 %\nVarsle hvis ingen data kommer inn'}
                rows={5}
                style={{ ...inputStyle, resize: 'vertical', minHeight: 120 }}
              />
            </Field>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <Field label="Hvor den leverer">
              <textarea
                value={destinationsInput}
                onChange={event => setDestinationsInput(event.target.value)}
                placeholder={'Én leveranse per linje\nChat-tråd i Sine\nUkentlig e-post til ledergruppen'}
                rows={4}
                style={{ ...inputStyle, resize: 'vertical', minHeight: 96 }}
              />
            </Field>

            <Field label="Rollup / fast oppsummering">
              <textarea
                value={rollup}
                onChange={event => setRollup(event.target.value)}
                placeholder="Hvordan skal den faste oppsummeringen se ut?"
                rows={4}
                style={{ ...inputStyle, resize: 'vertical', minHeight: 96 }}
              />
            </Field>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <Field label="Aktivering">
              <div style={{ display: 'grid', gap: 8 }}>
                {([
                  {
                    id: 'auto_run',
                    title: 'Auto-run',
                    body: 'Workflowen kjører og leverer direkte når triggeren inntreffer.',
                  },
                  {
                    id: 'manual_review',
                    title: 'Manuell review',
                    body: 'Workflowen samler opp signaler, men krever vurdering før neste steg.',
                  },
                ] as const).map(option => {
                  const active = activationMode === option.id
                  return (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => setActivationMode(option.id)}
                      style={{
                        textAlign: 'left',
                        padding: '12px 13px',
                        borderRadius: 10,
                        border: `1px solid ${active ? accent.border : '#2A2A2A'}`,
                        background: active ? accent.background : '#161616',
                        color: active ? '#F3F4F6' : '#CFCFCF',
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 4 }}>
                        <span style={{ fontSize: 12, fontWeight: 700 }}>{option.title}</span>
                        <span style={{ fontSize: 10, color: active ? accent.text : '#7A7A7A' }}>
                          {active ? 'Valgt' : workflowActivationLabel(option.id)}
                        </span>
                      </div>
                      <div style={{ fontSize: 12, lineHeight: 1.55, color: active ? '#D1D5DB' : '#8A8A8A' }}>
                        {option.body}
                      </div>
                    </button>
                  )
                })}
              </div>
            </Field>

            <Field label="Tilkobling (valgfritt)">
              <div style={{ position: 'relative' }}>
                <select
                  value={connectorId}
                  onChange={event => setConnectorId(event.target.value)}
                  style={{ ...inputStyle, appearance: 'none', paddingRight: 30 }}
                >
                  <option value="">Ingen tilkobling</option>
                  {CONNECTORS.map(connector => (
                    <option key={connector.id} value={connector.id}>{connector.name}</option>
                  ))}
                </select>
                <ChevronDown size={13} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: '#5A5A5A', pointerEvents: 'none' }} />
              </div>
            </Field>

            <Field label="Workflow-vindu">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={neverEnds}
                    onChange={event => setNeverEnds(event.target.checked)}
                    style={{ accentColor: '#1A93FE' }}
                  />
                  <span style={{ fontSize: 12, color: '#9A9A9A' }}>Slutter aldri</span>
                </label>
                {!neverEnds && (
                  <input
                    type="date"
                    value={endsAt}
                    onChange={event => setEndsAt(event.target.value)}
                    style={{ ...inputStyle, flex: 1 }}
                  />
                )}
              </div>
            </Field>
          </div>

          <div style={{
            padding: '12px 14px',
            borderRadius: 12,
            background: '#141414',
            border: '1px solid #262626',
          }}>
            <div style={{ fontSize: 11, color: '#7A7A7A', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
              Aktivering og oppfølging
            </div>
            <div style={{ fontSize: 13, color: '#E5E5E5', fontWeight: 600, marginBottom: 4 }}>
              {workflowActivationLabel(activationMode)}
            </div>
            <div style={{ fontSize: 12, color: '#A3A3A3', lineHeight: 1.55 }}>
              {workflowActivationCopy(activationMode)}
            </div>
          </div>

          <Field label="Agent-instruksjon *">
            <textarea
              value={agentPrompt}
              onChange={event => setAgentPrompt(event.target.value)}
              placeholder="Beskriv hva agenten skal gjøre i hver kjøring, hvordan den skal resonere, og hva den skal levere."
              rows={5}
              style={{ ...inputStyle, resize: 'vertical', minHeight: 120, fontFamily: 'monospace', fontSize: 12 }}
            />
          </Field>
        </div>

        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: 20,
          paddingTop: 16,
          borderTop: '1px solid #2A2A2A',
        }}>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: '1px solid #333',
              borderRadius: 8,
              color: '#7A7A7A',
              fontSize: 13,
              padding: '8px 16px',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Avbryt
          </button>

          <button
            onClick={handleSave}
            disabled={!canSave}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 7,
              padding: '9px 20px',
              borderRadius: 8,
              background: canSave ? '#F0F0F0' : '#252525',
              border: 'none',
              color: canSave ? '#111' : '#5A5A5A',
              fontSize: 13,
              fontWeight: 600,
              cursor: canSave ? 'pointer' : 'not-allowed',
              fontFamily: 'inherit',
              opacity: saving ? 0.7 : 1,
            }}
          >
            <Zap size={13} />
            {saving ? 'Lagrer...' : activationMode === 'manual_review' ? 'Lagre workflow for review' : 'Aktiver workflow'}
          </button>
        </div>
      </div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <label style={{ fontSize: 11, fontWeight: 600, color: '#5A5A5A', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {label}
      </label>
      {children}
    </div>
  )
}

function dayChipStyle(active: boolean): CSSProperties {
  return {
    padding: '4px 10px',
    borderRadius: 6,
    background: active ? '#1A93FE22' : '#252525',
    border: active ? '1px solid #1A93FE' : '1px solid #333',
    color: active ? '#1A93FE' : '#7A7A7A',
    fontSize: 11,
    cursor: 'pointer',
    fontFamily: 'inherit',
  }
}

const inputStyle: CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  background: '#252525',
  border: '1px solid #333',
  borderRadius: 8,
  color: '#E5E5E5',
  fontSize: 13,
  fontFamily: 'inherit',
  outline: 'none',
  boxSizing: 'border-box',
}
